import {
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  output,
  ResourceRef,
  ViewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { TableModule } from '@openng/optimus-ui/table';
import { ToggleSwitchModule } from '@openng/optimus-ui/toggleswitch';
import { SalesService } from '../../../../services/sales.service';
import { LinkedCoBuyerLotWithDetails } from '../../../../models/linked-co-buyer.models';
import { AddCoBuyerModalComponent } from '../../../add-co-buyer-modal/add-co-buyer-modal.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AbilityService, NgbModalService } from '@zayan-erp/shared';
import { SalesApsService } from '@zayan-erp/sales';
import { NgClass } from '@angular/common';

@Component({
  selector: 'linked-co-buyers-table',
  imports: [TableModule, ToggleSwitchModule, FormsModule, AddCoBuyerModalComponent, NgClass],
  templateUrl: './linked-co-buyers-table.component.html'
})
export class LinkedCoBuyersTableComponent {
  @ViewChild('addCoBuyerModal') addCoBuyerModal!: ElementRef;
  readonly #salesService = inject(SalesService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #modalService = inject(NgbModal);
  readonly #ngbModalService = inject(NgbModalService);
  readonly #abilityService = inject(AbilityService);
  readonly _salesApsService = inject(SalesApsService);
  readonly linkedCoBuyersResource = input.required<ResourceRef<LinkedCoBuyerLotWithDetails[]>>();
  readonly prospectId = input.required<string | null>();
  readonly coBuyerUnlinked = output<void>();

  readonly canWriteDeals = computed(() => this.#abilityService.ability.can('write-sales-deals', 'deals'));

  readonly linkedCoBuyers = computed(() => this.linkedCoBuyersResource().value() ?? []);

  openAddCoBuyerModal(): void {
    if (!this.canWriteDeals()) return;

    const modalRef = this.#modalService.open(this.addCoBuyerModal, {
      ...this.#ngbModalService.centerModal,
      size: 'xl'
    });
    modalRef.dismissed.subscribe(() => {
      this.#salesService.refreshTrigger.update(n => n + 1);
    });
  }

  onUnlinkCoBuyer(coBuyer: LinkedCoBuyerLotWithDetails): void {
    if (!this.canWriteDeals()) return;
    if (!coBuyer.id) return;

    this.#salesService
      .unlinkCoBuyerFromLot(coBuyer.id)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => this.coBuyerUnlinked.emit());
  }

  getFullName(coBuyer: LinkedCoBuyerLotWithDetails): string {
    return (
      [coBuyer.coBuyer.title, coBuyer.coBuyer.firstName, coBuyer.coBuyer.lastName].filter(Boolean).join(' ') || 'N/A'
    );
  }

  onToggleAddToMailingList(coBuyer: LinkedCoBuyerLotWithDetails, addToMailingList: boolean): void {
    if (!this.canWriteDeals()) return;
    if (!coBuyer.id) return;

    this.#salesService
      .updateCoBuyerMailingListStatus(coBuyer.id, addToMailingList)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: () => {
          this.linkedCoBuyersResource().reload();
        },
        error: () => {
          // Revert the toggle on error - the reload will restore the original value
          this.linkedCoBuyersResource().reload();
        }
      });
  }

  onToggleIncludeInContract(coBuyer: LinkedCoBuyerLotWithDetails, includeInContract: boolean): void {
    if (!this.canWriteDeals()) return;
    if (!coBuyer.id) return;

    this.#salesService
      .updateCoBuyerContractStatus(coBuyer.id, includeInContract)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: () => {
          this.linkedCoBuyersResource().reload();
        },
        error: () => {
          // Revert the toggle on error - the reload will restore the original value
          this.linkedCoBuyersResource().reload();
        }
      });
  }
}
